export type AmenityTranslation = [string, string, string];

// Space-level amenities: [Portuguese, English, Spanish]
// Source of truth: amenities-spaces.json (DB dump of space amenities)
export const SPACE_AMENITIES_TRANSLATIONS: AmenityTranslation[] = [
  [
    "Acessibilidade nas áreas comuns",
    "Accessibility in public areas",
    "Accesibilidad en las zonas comunes",
  ],
  ["Armário locker compartilhado", "Shared locker", "casillero compartido"],
  ["Sem Ar Condicionado", "No Air Conditioner", "Sin aire acondicionado"],
  ["Sem Wi-Fi", "No Wi-Fi", "No wifi"],
  ["Quadro branco", "Whiteboard", "Cuadro blanco"],
  ["Quadro branco", "White board", "Cuadro blanco"],
  ["Geladeira", "Refrigerator", "Refrigerador"],
  [
    "Acesso 24/7 às áreas comuns",
    "Access 24/7 to common areas",
    "Acceso 24/7 a las áreas comunes",
  ],
  ["Ar condicionado", "Air conditioner", "Aire condicionado"],
  ["Galeria de arte", "Art gallery", "Galería de arte"],
  ["Impressão P&B", "B&W print", "Impresión en blanco y negro"],
  ["Gerador de eletricidade", "Backup generator", "Generador eléctrico"],
  ["Varanda", "Balcony", "Balcón"],
  ["Cerveja", "Beer", "Cerveza"],
  ["Bicicletário", "Bike hack", "Aparcabicicletas"],
  ["Café", "Coffee", "Café"],
  ["Impressão Colorida", "Color print", "impresión a color"],
  ["Dog friendly", "Dog friendly", "Se admiten perros"],
  ["Área de alimentação", "Food hall", "Área de alimentación"],
  ["Salão de jogos", "Game room", "Salon de juegos"],
  ["Jardim / Terraço", "Garden / Terrace", "Jardín Terraza"],
  ["Adequado para crianças", "Kids friendly", "Apto para niños"],
  ["Biblioteca", "Library", "Biblioteca"],
  ["Espaço de convivência", "Living space", "Espacio vital"],
  ["Vestiário", "Locker room", "Guardarropa"],
  ["Espaço lounge", "Lounge area", "Espacio del Salón"],
  ["Sala de reunião", "Meeting room", "Sala de reuniones"],
  [
    "Estação móvel de trabalho",
    "Mobile work station",
    "Estación de trabajo móvil",
  ],
  ["Estacionamento", "Parking", "Estacionamiento"],
  ["Pet friendly", "Pet friendly", "Admite mascotas"],
  ["Cabine de call", "Phone booth", "Caja de llamadas"],
  ["Recepção", "Reception", "Recepción"],
  ["Sala de descompressão", "Relaxation room", "Sala de descompresión"],
  ["Restaurante", "Restaurant", "Restaurante"],
  ["Digitalizadora", "Scanner", "Escáner"],
  ["Cozinha compartilhada", "Shared kitchen", "Cocina compartida"],
  ["Lanches", "Snacks", "Aperitivos"],
  ["Piscina", "Swimming pool", "Piscina"],
  ["Chá", "Tea", "té"],
  ["Realizamos eventos", "We carry out events", "Hacemos eventos"],
  ["Wi-Fi", "Wi-Fi", "Wi-Fi"],
];

// Room-level amenities: slug → Portuguese name
// Source of truth: amenities-rooms.json
const ROOM_AMENITY_SLUG_MAP: Record<string, string> = {
  "acesso-24-7": "Acesso 24/7",
  "controle-de-acesso-digital": "Controle de acesso digital",
  acessibilidade: "Acessibilidade",
  "ar-condicionado": "Ar-condicionado",
  mobiliado: "Mobiliado",
  "cortina-ou-persiana": "Cortina ou persiana",
  "portas-automaticas": "Portas automáticas",
  "sala-climatizada": "Sala climatizada",
  "vidro-com-isolamento-acustico": "Vidro com isolamento acústico",
  gerador: "Gerador",
  "endereco-comercial": "Endereço comercial",
  "endereco-fiscal": "Endereço fiscal",
  "escritorio-virtual": "Escritório virtual",
  "limpeza-da-sala": "Limpeza da sala",
  "cafe-no-espaco": "Café no espaço",
  copa: "Copa",
  "armario-locker": "Armário locker",
  telefone: "Telefone",
  "cabine-de-call": "Cabine de call",
  "banheiro-privado": "Banheiro privado",
  "sala-de-reuniao": "Sala de reunião",
  "quadro-branco": "Quadro branco",
  janela: "Janela",
  armario: "Armário",
  "cafe-na-sala": "Café na sala",
  "certificacao-do-predio-leed-gold-etc":
    "Certificação do prédio: leed gold, etc.",
  "cozinha-equipada": "Cozinha equipada",
  "detectores-de-fumaca": "Detectores de fumaça",
  frigobar: "Frigobar",
  auditorio: "Auditório",
  "estacionamento-no-local": "Estacionamento no local",
  "estacionamento-proximo": "Estacionamento próximo",
  "impressao-colorida": "Impressão colorida",
  "impressao-pb": "Impressão P&B",
  "vpn-private-network-rede-privada": "VPN - Private network (rede privada)",
  "scanner-digitalizadora": "Scanner / Digitalizadora",
  "wi-fi": "Wi-Fi",
  impressora: "Impressora",
  "internet-cabeada": "Internet cabeada",
  "link-dedicado": "Link dedicado",
  "cftv-circuito-fechado-de-tv": "CFTV (circuito fechado de TV)",
  recepcionista: "Recepcionista",
  "seguranca-monitorada-24h": "Segurança monitorada 24h",
  "sistemas-de-sprinklers": "Sistemas de sprinklers",
  "vigilancia-nas-areas-comuns-e-estacionamento":
    "Vigilância nas áreas comuns e estacionamento",
};

/**
 * Room-level amenity slugs treated as "priority" in the proposal drawer —
 * shown first, always visible; everything else collapses under "Outras"
 * until expanded. This is the single source of truth for that grouping so
 * any UI listing room amenities stays consistent instead of picking its own
 * subset.
 */
export const PRIORITY_ROOM_AMENITY_SLUGS = [
  "wi-fi",
  "endereco-fiscal",
  "impressora",
  "telefone",
  "ar-condicionado",
  "quadro-branco",
  "armario",
  "banheiro-privado",
  "cafe-na-sala",
  "cafe-no-espaco",
  "cafe-e-agua",
  "copa",
  "recepcao",
  "estacionamento-no-local",
  "estacionamento-proximo",
  "acessibilidade",
];

const createTranslationMap = (): Map<string, string> => {
  const map = new Map<string, string>();

  SPACE_AMENITIES_TRANSLATIONS.forEach(([pt, en, es]) => {
    const normalizedPt = pt.toLowerCase().trim();
    const normalizedEn = en.toLowerCase().trim();
    const normalizedEs = es.toLowerCase().trim();

    map.set(normalizedPt, pt);
    map.set(normalizedEn, pt);
    map.set(normalizedEs, pt);
  });

  return map;
};

const translationMap = createTranslationMap();

const slugToPhrase = (slug: string): string =>
  slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Translate space-level amenities (English/Spanish text) to Portuguese.
 * Space amenities come from the DB as comma-separated English strings.
 * Fallback: tries room slug map, then converts to readable phrase.
 */
export const translateSpaceAmenity = (amenity: string): string => {
  const normalized = amenity.toLowerCase().trim();
  if (translationMap.has(normalized)) return translationMap.get(normalized)!;
  if (ROOM_AMENITY_SLUG_MAP[amenity]) return ROOM_AMENITY_SLUG_MAP[amenity];
  return slugToPhrase(amenity);
};

/** @deprecated Use translateSpaceAmenity instead */
export const translateAmenityToPortuguese = translateSpaceAmenity;

/**
 * Space-level amenity labels (canonical Portuguese form) treated as
 * "priority" in the proposal drawer — mirrors `PRIORITY_ROOM_AMENITY_SLUGS`
 * but keyed by label instead of slug, since space amenities come from the
 * DB as full words in pt/en/es rather than slugs. Canonicalize any input
 * with `translateSpaceAmenity` before comparing.
 */
export const PRIORITY_SPACE_AMENITY_LABELS = [
  "Recepção",
  "Estacionamento",
  "Acesso 24/7 às áreas comuns",
  "Ar condicionado",
  "Wi-Fi",
  "Sala de reunião",
  "Cozinha compartilhada",
  "Bicicletário",
];

export const isPrioritySpaceAmenity = (amenity: string): boolean =>
  PRIORITY_SPACE_AMENITY_LABELS.includes(translateSpaceAmenity(amenity));

/**
 * Translate room-level amenities (slugs) to Portuguese.
 * Room amenities come from the API as slugs like "ar-condicionado".
 * Fallback: tries space translation map, then converts slug to readable phrase.
 */
export const translateRoomAmenity = (slug: string): string => {
  if (ROOM_AMENITY_SLUG_MAP[slug]) return ROOM_AMENITY_SLUG_MAP[slug];
  const normalized = slug.toLowerCase().trim();
  if (translationMap.has(normalized)) return translationMap.get(normalized)!;
  return slugToPhrase(slug);
};

/** @deprecated Use translateRoomAmenity instead */
export const translateAmenitySlug = translateRoomAmenity;

/* ── Locale-aware variants (used by proposal i18n) ─────────────────── */

import type { ProposalLocale } from "../proposal-standard/i18n/types";

const LOCALE_INDEX: Record<ProposalLocale, number> = { pt: 0, en: 1, es: 2 };

const createLocaleTranslationMap = (
  locale: ProposalLocale,
): Map<string, string> => {
  const idx = LOCALE_INDEX[locale];
  const map = new Map<string, string>();

  SPACE_AMENITIES_TRANSLATIONS.forEach(([pt, en, es]) => {
    const target = [pt, en, es][idx];
    // Map all normalized forms to the target locale
    map.set(pt.toLowerCase().trim(), target);
    map.set(en.toLowerCase().trim(), target);
    map.set(es.toLowerCase().trim(), target);
  });

  return map;
};

// Cache maps per locale
const localeMapCache: Partial<Record<ProposalLocale, Map<string, string>>> = {};

function getLocaleMap(locale: ProposalLocale): Map<string, string> {
  if (!localeMapCache[locale]) {
    localeMapCache[locale] = createLocaleTranslationMap(locale);
  }
  return localeMapCache[locale]!;
}

// Room amenity slug translations for EN and ES
const ROOM_AMENITY_SLUG_MAP_EN: Record<string, string> = {
  "acesso-24-7": "24/7 Access",
  "controle-de-acesso-digital": "Digital access control",
  acessibilidade: "Accessibility",
  "ar-condicionado": "Air conditioning",
  mobiliado: "Furnished",
  "cortina-ou-persiana": "Curtain or blinds",
  "portas-automaticas": "Automatic doors",
  "sala-climatizada": "Climate-controlled room",
  "vidro-com-isolamento-acustico": "Soundproof glass",
  gerador: "Generator",
  "endereco-comercial": "Business address",
  "endereco-fiscal": "Fiscal address",
  "escritorio-virtual": "Virtual office",
  "limpeza-da-sala": "Room cleaning",
  "cafe-no-espaco": "Coffee in the space",
  copa: "Pantry",
  "armario-locker": "Locker",
  telefone: "Phone",
  "cabine-de-call": "Phone booth",
  "banheiro-privado": "Private bathroom",
  "sala-de-reuniao": "Meeting room",
  "quadro-branco": "Whiteboard",
  janela: "Window",
  armario: "Cabinet",
  "cafe-na-sala": "Coffee in the room",
  "certificacao-do-predio-leed-gold-etc":
    "Building certification: LEED Gold, etc.",
  "cozinha-equipada": "Equipped kitchen",
  "detectores-de-fumaca": "Smoke detectors",
  frigobar: "Mini fridge",
  auditorio: "Auditorium",
  "estacionamento-no-local": "On-site parking",
  "estacionamento-proximo": "Nearby parking",
  "impressao-colorida": "Color printing",
  "impressao-pb": "B&W printing",
  "vpn-private-network-rede-privada": "VPN - Private network",
  "scanner-digitalizadora": "Scanner",
  "wi-fi": "Wi-Fi",
  impressora: "Printer",
  "internet-cabeada": "Wired internet",
  "link-dedicado": "Dedicated link",
  "cftv-circuito-fechado-de-tv": "CCTV",
  recepcionista: "Receptionist",
  "seguranca-monitorada-24h": "24h monitored security",
  "sistemas-de-sprinklers": "Sprinkler systems",
  "vigilancia-nas-areas-comuns-e-estacionamento":
    "Surveillance in common areas and parking",
};

const ROOM_AMENITY_SLUG_MAP_ES: Record<string, string> = {
  "acesso-24-7": "Acceso 24/7",
  "controle-de-acesso-digital": "Control de acceso digital",
  acessibilidade: "Accesibilidad",
  "ar-condicionado": "Aire acondicionado",
  mobiliado: "Amueblado",
  "cortina-ou-persiana": "Cortina o persiana",
  "portas-automaticas": "Puertas automáticas",
  "sala-climatizada": "Sala climatizada",
  "vidro-com-isolamento-acustico": "Vidrio con aislamiento acústico",
  gerador: "Generador",
  "endereco-comercial": "Dirección comercial",
  "endereco-fiscal": "Dirección fiscal",
  "escritorio-virtual": "Oficina virtual",
  "limpeza-da-sala": "Limpieza de la sala",
  "cafe-no-espaco": "Café en el espacio",
  copa: "Cocina",
  "armario-locker": "Casillero",
  telefone: "Teléfono",
  "cabine-de-call": "Cabina de llamadas",
  "banheiro-privado": "Baño privado",
  "sala-de-reuniao": "Sala de reuniones",
  "quadro-branco": "Pizarra blanca",
  janela: "Ventana",
  armario: "Armario",
  "cafe-na-sala": "Café en la sala",
  "certificacao-do-predio-leed-gold-etc":
    "Certificación del edificio: LEED Gold, etc.",
  "cozinha-equipada": "Cocina equipada",
  "detectores-de-fumaca": "Detectores de humo",
  frigobar: "Minibar",
  auditorio: "Auditorio",
  "estacionamento-no-local": "Estacionamiento en el lugar",
  "estacionamento-proximo": "Estacionamiento cercano",
  "impressao-colorida": "Impresión a color",
  "impressao-pb": "Impresión en blanco y negro",
  "vpn-private-network-rede-privada": "VPN - Red privada",
  "scanner-digitalizadora": "Escáner",
  "wi-fi": "Wi-Fi",
  impressora: "Impresora",
  "internet-cabeada": "Internet por cable",
  "link-dedicado": "Enlace dedicado",
  "cftv-circuito-fechado-de-tv": "CCTV",
  recepcionista: "Recepcionista",
  "seguranca-monitorada-24h": "Seguridad monitoreada 24h",
  "sistemas-de-sprinklers": "Sistemas de rociadores",
  "vigilancia-nas-areas-comuns-e-estacionamento":
    "Vigilancia en áreas comunes y estacionamiento",
};

const ROOM_AMENITY_SLUG_MAPS: Record<ProposalLocale, Record<string, string>> = {
  pt: ROOM_AMENITY_SLUG_MAP,
  en: ROOM_AMENITY_SLUG_MAP_EN,
  es: ROOM_AMENITY_SLUG_MAP_ES,
};

/**
 * Translate space-level amenities with locale support.
 */
export const translateSpaceAmenityByLocale = (
  amenity: string,
  locale: ProposalLocale = "pt",
): string => {
  const localeMap = getLocaleMap(locale);
  const normalized = amenity.toLowerCase().trim();
  if (localeMap.has(normalized)) return localeMap.get(normalized)!;

  const slugMap = ROOM_AMENITY_SLUG_MAPS[locale];
  if (slugMap[amenity]) return slugMap[amenity];

  // Fallback to PT
  if (locale !== "pt") {
    return translateSpaceAmenity(amenity);
  }
  return slugToPhrase(amenity);
};

/**
 * Translate room-level amenities (slugs) with locale support.
 */
export const translateRoomAmenityByLocale = (
  slug: string,
  locale: ProposalLocale = "pt",
): string => {
  const slugMap = ROOM_AMENITY_SLUG_MAPS[locale];
  if (slugMap[slug]) return slugMap[slug];

  const localeMap = getLocaleMap(locale);
  const normalized = slug.toLowerCase().trim();
  if (localeMap.has(normalized)) return localeMap.get(normalized)!;

  // Fallback to PT
  if (locale !== "pt") {
    return translateRoomAmenity(slug);
  }
  return slugToPhrase(slug);
};
